import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormHelperText,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useGetAllEnumListQuery, useUploadFormMutation } from '@/api/common';

import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import {
	FormManagementType,
	FormManagementTypeEnum,
} from '@/api/enum/common.enum';

interface Props {
	type: FormManagementType;
	open: boolean;
	userId: string;
	onClose: () => void;
}
export default function UploadFormDialog(props: Props) {
	const { type, onClose, open, userId } = props;
	const { t } = useTranslation();
	const [uploadForm] = useUploadFormMutation();
	const { familyId } = useParams();
	const showModal = useModal();

	useEffect(() => {
		methods.setValue('formType', FormManagementTypeEnum[type]);
		console.log(type);
	}, [type]);

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		formType: Yup.string().required(t('errorMessage.pleaseSelect')),
		files: Yup.string().required(t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	const filesField = useController({
		name: 'files',
		control: methods.control,
	});

	const watchFiles = methods.watch('files');

	const onSubmit: SubmitHandler<any> = async (data) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.formBeenUploaded')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.formBeenUploadedText')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const formData = new FormData();
					formData.append('files', methods.getValues('files'));
					formData.append('userId', familyId || '');
					formData.append('formType', methods.getValues('formType'));
					data.description && formData.append('description', data.description);

					const response = await uploadForm(formData);

					if ('data' in response) {
						enqueueSnackbar(t('snackbar.uploadSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						handleClose();
					}
				}}
			/>
		));
	};

	const handleClose = () => {
		methods.reset();
		onClose();
	};

	const handleUpload = (file: any) => {
		const isValidPdf =
			file[0].type === 'application/pdf' ||
			file[0].name.toLowerCase().endsWith('.pdf');

		if (!isValidPdf) {
			enqueueSnackbar(t('snackbar.pleaseUploadPdf'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			methods.setValue('files', null);
			return;
		}
		methods.setValue('files', file[0]);
		methods.trigger();
	};

	const disabledFormType = () => {
		if (type == FormManagementType.all) return false;
		return true;
	};

	const formContent = (
		<>
			{/* <Button
				endIcon={<FileUploadOutlinedIcon />}
				onClick={() => setOpen(true)}
				variant='contained'
			>
				{t('button.uploadForm')}
			</Button> */}
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='sm'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('button.uploadForm')}</DialogTitle>

				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' color='common.neutral40'>
						{t('preAdmission.selectDocuments')}
					</Typography>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Box sx={{ margin: '0px 0 10px' }}>
								{!watchFiles && (
									<Button
										component='label'
										role={undefined}
										variant='outlined'
										tabIndex={-1}
										endIcon={<CloudUploadIcon />}
										sx={{ border: 'none' }}
									>
										{t('button.UploadFile')}
										<VisuallyHiddenInput
											type='file'
											onChange={(event: any) =>
												handleUpload(event.target.files)
											}
											multiple
											accept='.pdf'
										/>
									</Button>
								)}
								{watchFiles && (
									<Box
										display={'flex'}
										alignItems={'center'}
										paddingLeft={'15px'}
									>
										<Typography
											variant='body1'
											sx={{
												color: '#1BBED7',
												textDecoration: 'underline',
											}}
										>
											{watchFiles.name}
										</Typography>
										<IconButton
											sx={{ color: '#1BBED7' }}
											onClick={() => methods.setValue('files', null)}
										>
											<CancelOutlinedIcon />
										</IconButton>
									</Box>
								)}
								<FormHelperText color='error' style={{ color: '#FF5B4D' }}>
									{filesField.fieldState.error
										? t('errorMessage.pleaseSelect')
										: ''}
								</FormHelperText>
							</Box>

							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={12}>
									<AppDropDownMenu
										disabled={disabledFormType()}
										optional={true}
										control={methods.control}
										name='formType'
										label={t('preAdmission.typeOfForm')}
										options={[
											{
												id: 6,
												name: 'Family member / Carer Member Application Form',
											},
										]}
									/>
								</Grid>
								<Grid size={12}>
									<AppTextField
										control={methods.control}
										name='description'
										label={t('preAdmission.description')}
										maxLength={50}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>

					<Button
						sx={{ textTransform: 'none' }}
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
